import React, { useState } from 'react';
import { ShoppingCart, Menu, X, Phone, Mail, Instagram, Twitter, Facebook, MessageCircle, ChevronDown, ChevronUp, Star } from 'lucide-react';

const ZuriWater = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(null);

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const colors = {
    primary: '#000D6B',
    secondary: 'white',
    accent1: '#FA8128',
    accent2: '#F28500',
    callBtn: '#000D6B',
    whatsapp: '#F9A826',
    textDark: '#0a0a0a',
    textLight: '#f4f4f4'
  };

  const products = [
    {
      id: 1,
      title: "19L Dispenser Refill",
      image: "https://images.unsplash.com/photo-1562255740-d99625345722?auto=format&fit=crop&q=80&w=800",
      details: [
        "19 Litre standard dispenser size",
        "Triple-filtered Reverse Osmosis",
        "Returnable eco-friendly bottle"
      ]
    },
    {
      id: 2,
      title: "10L Family Size",
      image: "https://images.unsplash.com/photo-1621800176463-54877e5e3477?auto=format&fit=crop&q=80&w=800",
      details: [
        "10 Litre easy-carry handle",
        "Perfect for kitchen counters",
        "Disposable recyclable PET"
      ]
    },
    {
      id: 3,
      title: "500ml Executive Pack",
      image: "https://images.unsplash.com/photo-1625126596924-f5a8940e53a9?auto=format&fit=crop&q=80&w=800",
      details: [
        "Case of 24 bottles (500ml)",
        "Premium sealed cap",
        "Ideal for meetings & events"
      ]
    },
    {
      id: 4,
      title: "1L Sports Bottle",
      image: "https://images.unsplash.com/photo-1602143407151-011141959309?auto=format&fit=crop&q=80&w=800",
      details: [
        "1 Litre hydration capacity",
        "Ergonomic grip design",
        "Fits gym bag pockets"
      ]
    },
    {
      id: 5,
      title: "5L Daily Value",
      image: "https://images.unsplash.com/photo-1599309062973-207040445a6c?auto=format&fit=crop&q=80&w=800",
      details: [
        "5 Litre convenient size",
        "Sturdy handle for pouring",
        "Great for camping trips"
      ]
    },
    {
      id: 6,
      title: "Custom Branded",
      image: "https://images.unsplash.com/photo-1616118132534-381148898bb8?auto=format&fit=crop&q=80&w=800",
      details: [
        "Your logo on our 500ml bottles",
        "Perfect for corporate events",
        "High-quality label printing"
      ]
    }
  ];

  const faqs = [
    {
      q: "Do you offer free delivery?",
      a: "Yes, we offer free delivery for orders of 3 or more 19L refills within the city limits. Smaller orders may attract a minimal delivery fee."
    },
    {
      q: "How is the water purified?",
      a: "Our water undergoes a rigorous 7-step purification process, including Reverse Osmosis (RO), UV sterilization, and Ozonation to ensure absolute purity."
    },
    {
      q: "Can I return the plastic bottles?",
      a: "The 19L dispenser bottles are returnable and refilled. Our smaller PET bottles (500ml, 1L, 10L) are 100% recyclable but not refillable by us for hygiene reasons."
    },
    {
      q: "Do you supply corporate events?",
      a: "Absolutely! We specialize in bulk orders and can even provide custom-branded water bottles with your company logo for weddings, conferences, and events."
    }
  ];

  const testimonials = [
    {
      name: "Sarah K.",
      role: "Homeowner",
      text: "The 19L refill service is a lifesaver. The delivery team is always punctual, and the water tastes incredibly fresh compared to other brands.",
      rating: 5
    },
    {
      name: "James M.",
      role: "Office Manager",
      text: "We switched our office supply to Zuri Water three months ago. The custom pricing for bulk orders helps our budget, and the staff is very professional.",
      rating: 5
    },
    {
      name: "Hotel Relax",
      role: "Hospitality Partner",
      text: "Zuri provides all the 500ml bottles for our guest rooms. The packaging looks premium and fits perfectly with our hotel's aesthetic.",
      rating: 4
    }
  ];

  return (
    <div className="zuri-container">
      <style>{`
        :root {
          --primary: ${colors.primary};
          --secondary: ${colors.secondary};
          --accent-1: ${colors.accent1};
          --accent-2: ${colors.accent2};
          --call-btn: ${colors.callBtn};
          --whatsapp-btn: ${colors.whatsapp};
          --text-main: ${colors.textDark};
          --card-radius: 16px;
        }

        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
          font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        }

        body {
          background-color: #f9fafb;
          color: var(--text-main);
          overflow-x: hidden;
        }

        .text-center { text-align: center; }
        .container { max-width: 1200px; margin: 0 auto; padding: 0 2rem; }

        .hero {
          position: relative;
          height: 85vh;
          min-height: 600px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          background: linear-gradient(rgba(0, 13, 107, 0.75), rgba(0, 13, 107, 0.6)),
            url('https://images.unsplash.com/photo-1548839140-29a749e1cf4d?auto=format&fit=crop&q=80&w=2000');
          background-size: cover;
          background-position: center;
          color: var(--secondary);
        }

        .hero-content {
          max-width: 800px;
          padding: 2rem;
          animation: slideUp 1s ease-out;
        }

        .hero h1 {
          font-size: 4rem;
          margin-bottom: 1.5rem;
          font-weight: 800;
          line-height: 1.1;
        }

        .hero p {
          font-size: 1.3rem;
          margin-bottom: 2.5rem;
          opacity: 0.95;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        .btn-cta-main {
          background-color: var(--accent-1);
          color: var(--secondary);
          padding: 1rem 3rem;
          font-size: 1.2rem;
          border: none;
          border-radius: 50px;
          cursor: pointer;
          font-weight: bold;
          transition: transform 0.2s, background-color 0.2s;
          text-decoration: none;
          display: inline-block;
          box-shadow: 0 4px 15px rgba(250, 129, 40, 0.5);
        }

        .btn-cta-main:hover {
          background-color: var(--accent-2);
          transform: translateY(-3px);
        }

        .section { padding: 5rem 0; }
        .bg-light { background-color: #f9fafb; }
        .bg-white { background-color: #ffffff; }

        .section-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .section-header h2 {
          color: var(--primary);
          font-size: 2.5rem;
          margin-bottom: 1rem;
          font-weight: 700;
        }

        .section-header p {
          color: #666;
          max-width: 600px;
          margin: 0 auto;
          font-size: 1.1rem;
        }

        .grid-container {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2.5rem;
        }

        .product-card {
          background: white;
          border-radius: var(--card-radius);
          overflow: hidden;
          box-shadow: 0 10px 25px rgba(0,0,0,0.05);
          transition: transform 0.3s ease;
          display: flex;
          flex-direction: column;
        }

        .product-card:hover { transform: translateY(-8px); }

        .card-image-wrapper {
          height: 280px;
          overflow: hidden;
          background-color: #f0f4f8;
        }

        .card-image-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .product-card:hover .card-image-wrapper img { transform: scale(1.05); }

        .card-content {
          padding: 2rem;
          text-align: left;
          flex-grow: 1;
          display: flex;
          flex-direction: column;
        }

        .card-title {
          color: var(--primary);
          font-size: 1.5rem;
          margin-bottom: 1rem;
          font-weight: 700;
          text-align: center;
        }

        .card-details {
          margin-bottom: 1.5rem;
          color: #555;
        }

        .card-details ol {
          padding-left: 1.2rem;
        }

        .card-details li {
          margin-bottom: 0.5rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px dashed #eee;
        }

        .card-details li:last-child { border-bottom: none; }

        .action-buttons {
          margin-top: auto;
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
        }

        .btn-call {
          background-color: var(--call-btn);
          color: white;
          border: none;
          padding: 0.8rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          text-decoration: none;
          transition: opacity 0.2s;
        }

        .btn-whatsapp {
          background-color: var(--whatsapp-btn);
          color: white;
          border: none;
          padding: 0.8rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          text-decoration: none;
          transition: opacity 0.2s;
        }

        .btn-call:hover, .btn-whatsapp:hover { opacity: 0.9; }

        .info-section {
          background: white;
          padding: 5rem 0;
          border-top: 1px solid #e2e8f0;
        }

        .split-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .faq-side, .testimonial-side {
          display: flex;
          flex-direction: column;
        }

        .subsection-header {
          text-align: left;
          margin-bottom: 2rem;
        }

        .subsection-title {
          font-size: 1.8rem;
          font-weight: 700;
          color: var(--primary);
          margin-bottom: 0.5rem;
        }

        .subsection-desc {
          font-size: 1rem;
          color: #64748b;
        }

        .faq-item {
          margin-bottom: 1.5rem;
          border-bottom: 1px solid #f1f5f9;
          padding-bottom: 1.5rem;
        }

        .faq-q {
          font-weight: 700;
          color: var(--primary);
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 0.8rem;
          font-size: 1rem;
          cursor: pointer;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          padding: 0;
        }

        .faq-a {
          color: #64748b;
          line-height: 1.6;
          font-size: 0.95rem;
        }

        .testimonial-card {
          background: #f8fafc;
          padding: 2rem;
          border-radius: 16px;
          margin-bottom: 1.5rem;
          border: 1px solid #eef2f6;
        }

        .testimonial-card p {
          font-style: italic;
          color: #475569;
          margin: 0 0 1rem 0;
          line-height: 1.6;
          font-size: 0.95rem;
        }

        .testimonial-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .testimonial-author {
          font-weight: 700;
          font-size: 0.9rem;
          color: var(--primary);
        }

        .stars {
          display: flex;
          gap: 2px;
        }

        .copyright-bar {
          background-color: var(--primary);
          color: var(--secondary);
          padding: 2rem;
          text-align: center;
          font-size: 0.9rem;
          opacity: 0.9;
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

        @media (max-width: 1024px) {
          .grid-container { grid-template-columns: repeat(2, 1fr); }
          .split-layout { gap: 3rem; }
          .hero h1 { font-size: 3rem; }
        }

        @media (max-width: 768px) {
          .hero h1 { font-size: 2.5rem; }
          .grid-container { grid-template-columns: 1fr; }
          .split-layout { grid-template-columns: 1fr; gap: 3rem; }
          .card-image-wrapper { height: 220px; }
          .section { padding: 3rem 0; }
        }
      `}
      </style>

      <section className="hero" id="home">
        <div className="hero-content">
          <h1>Pure Refreshment, <br />Delivered.</h1>
          <p>Premium bottled water for homes, offices, and events. Experience the taste of purity in every drop.</p>
          <a href="#products" className="btn-cta-main">View Products</a>
        </div>
      </section>

      <section className="section bg-light" id="products">
        <div className="container">
          <div className="section-header">
            <h2>Our Products</h2>
            <p>From 19L dispensers to on-the-go bottles, we have the perfect size for every need.</p>
          </div>
          <div className="grid-container">
            {products.map((product) => (
              <div className="product-card" key={product.id}>
                <div className="card-image-wrapper">
                  <img src={product.image} alt={product.title} />
                </div>
                <div className="card-content">
                  <h3 className="card-title">{product.title}</h3>
                  <div className="card-details">
                    <ol>
                      {product.details.map((detail, index) => (
                        <li key={index}>{detail}</li>
                      ))}
                    </ol>
                  </div>
                  <div className="action-buttons">
                    <a href="tel:0754906060" className="btn-call">
                      <Phone size={18} /> Request Call
                    </a>
                    <a href="https://wa.me/254754906060" className="btn-whatsapp">
                      <MessageCircle size={18} /> Order Now
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="info-section" id="faq-reviews">
        <div className="container">
          <div className="section-header">
            <h2>Customer Reviews & FAQs</h2>
            <p>What our customers say and common questions answered.</p>
          </div>
          
          <div className="split-layout">
            <div className="faq-side">
              <div className="subsection-header">
                <h2 className="subsection-title">Common Questions</h2>
                <p className="subsection-desc">Everything you need to know</p>
              </div>
              {faqs.map((item, index) => (
                <div key={index} className="faq-item">
                  <button className="faq-q" onClick={() => toggleAccordion(index)}>
                    {item.q}
                    <ChevronDown size={18} color={colors.whatsapp} />
                  </button>
                  {activeAccordion === index && (
                    <div className="faq-a">
                      {item.a}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="testimonial-side">
              <div className="subsection-header">
                <h2 className="subsection-title">Happy Customers</h2>
                <p className="subsection-desc">Don't just take our word for it</p>
              </div>
              {testimonials.map((review, i) => (
                <div key={i} className="testimonial-card">
                  <p>"{review.text}"</p>
                  <div className="testimonial-footer">
                    <span className="testimonial-author">— {review.name}</span>
                    <div className="stars">
                      {[...Array(review.rating)].map((_, idx) => (
                        <Star key={idx} size={14} fill={colors.whatsapp} color="none" />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ZuriWater;